/**
 * reportPresets — preset configurations for "item-query" reports.
 *
 * Phase 4 of the reports reorganization adds new reports as PRESETS over the
 * existing custom-report engine rather than as bespoke aggregation pages. A
 * preset is a starting filter/sort/group configuration keyed by report key; a
 * home card links to `/reports/custom/new?preset=<key>`, and CustomReportPage
 * seeds the designer from the matching preset. Because these ride the item-list
 * engine, they inherit the item-field registry (Phase 2): any field added to the
 * item list is immediately available to every preset.
 *
 * Presets seed FILTERS, SORT, and GROUPING only — not column visibility (which is
 * a user-global, localStorage-persisted preference shared with the item list;
 * see useItemListState). Field ids reference the item-field registry
 * (config/itemFields.ts); a test asserts every referenced field is real.
 *
 * These configs are code-defined and easily tuned. Reports that need non-item
 * data (Decisions & Commitments, Catchball Health, Capacity vs Commitment) are
 * NOT presets — they require their own data sources and are separate builds.
 */

import type { FilterRule, SortSpec } from '../components/prioritization/item-list/hooks/useItemListState';

/** A filter group in a preset — rules omit `id` (assigned when applied via addRule). */
export interface PresetFilterGroup {
  conjunction: 'AND' | 'OR';
  rules: Omit<FilterRule, 'id'>[];
}

export interface ReportPresetConfig {
  sort?: SortSpec[];
  groupBy?: string | null;
  filterGroups?: PresetFilterGroup[];
}

const ACTIVE_ONLY: PresetFilterGroup = {
  conjunction: 'AND',
  rules: [
    { field: 'exit_status', label: 'Status', operator: 'in', value: ['active'], displayValue: 'Active' },
  ],
};

export const REPORT_PRESETS: Record<string, ReportPresetConfig> = {
  // Active items not yet base-complete, grouped by category — the intake backlog.
  'intake-clarity': {
    groupBy: 'itemCategory',
    sort: [{ field: 'createdAt', direction: 'desc' }],
    filterGroups: [
      {
        conjunction: 'AND',
        rules: [
          { field: 'exit_status', label: 'Status', operator: 'in', value: ['active'], displayValue: 'Active' },
          { field: 'readyForRanking', label: 'Base complete', operator: 'boolean', value: false, displayValue: 'No' },
        ],
      },
    ],
  },

  // Active items distributed across strategic buckets — coverage/gaps vs strategy.
  'strategy-alignment': {
    groupBy: 'strategicBucket',
    sort: [{ field: 'updatedAt', direction: 'desc' }],
    filterGroups: [ACTIVE_ONLY],
  },
};

export function getReportPreset(key: string | null | undefined): ReportPresetConfig | undefined {
  return key ? REPORT_PRESETS[key] : undefined;
}

// ============================================================================
// Starter sentences — the empty Report Designer
// ============================================================================

/**
 * A sentence a person might say, and the statement it means. The empty Report
 * Designer draws these the way Automations draws its starter rules: click one
 * and its statement goes into the statement bar, where the table redraws and
 * the person can change any part of it before saving. The statement is written
 * here, not asked of a model, so a starter works with no AI key at all. A test
 * parses each one against the item list's fields as the emptiest workspace
 * resolves them: the Queue group (where Owner lives) and the Activity group are
 * left out until the workspace has queues, cycles or tracks, so no starter
 * uses those fields.
 */
export interface ReportStarter {
  sentence: string;
  statement: string;
}

export const REPORT_STARTERS: ReportStarter[] = [
  {
    sentence: 'Active items added in the last 30 days, newest first',
    statement: 'exit_status = active AND createdAt >= -30d ORDER BY createdAt DESC',
  },
  {
    sentence: 'Active risks and problems, most recently changed first',
    statement: 'itemCategory IN (problem, risk) AND exit_status = active ORDER BY updatedAt DESC',
  },
  {
    sentence: 'Active items nobody has touched in a month, oldest first',
    statement: 'exit_status = active AND updatedAt < -30d ORDER BY updatedAt ASC',
  },
];
